// ============================================================================
// Finance Recent Changes — AFB-0045 (Jose/Troy).
//
// A finance-facing feed of recent material changes: sub swaps, rate changes,
// terminations, new-account starts, margin flags, invoice gaps, reimbursables.
// Source of truth = get_finance_recent_changes(p_days) RPC, which returns the
// notifications whose event_type is actively routed to role 'finance' in
// notification_routing — so adding a routing row automatically extends this
// feed with no frontend change.
// ============================================================================

import { hasFinanceCapability } from '../../shared/role-gates';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PageHeader,
  EmptyState,
  ErrorState,
  Skeleton,
} from '../../shared/components';
import { useAuth } from '../../shared/auth';
import { supabase } from '../../shared/supabase';
import { rpcErrorMessage } from '../../shared/rpc-error';


interface ChangeRow {
  notification_id: string;
  event_type: string;
  entity_type: string;
  entity_id: string;
  title: string | null;
  body: string | null;
  actor_eid: string | null;
  actor_name: string | null;
  created_at: string;
  target_url: string | null;
}

const EVENT_LABELS: Record<string, string> = {
  sub_changed: 'Sub changed',
  sub_rate_changed: 'Contract rate',
  client_rate_changed: 'Client rate',
  fincomm_open: 'Billing comm',
  facility_terminated: 'Facility closed',
  account_terminated: 'Account terminated',
  new_account_started: 'New account',
  ready_to_invoice: 'Ready to invoice',
  margin_red: 'Margin RED',
  invoice_gap_detected: 'Invoice gap',
  startup_cost_finalized: 'Startup cost',
  reimbursable_line_delivered: 'Reimbursable',
  new_sub_onboarded: 'Sub setup complete',
  coi_t30: 'COI <30d',
  coi_t7: 'COI <7d',
  coi_expired: 'COI expired',
};

const EVENT_TONE: Record<string, string> = {
  margin_red: 'bg-st-danger-bg-strong text-st-danger-fg-strong',
  coi_expired: 'bg-st-danger-bg-strong text-st-danger-fg-strong',
  invoice_gap_detected: 'bg-st-warn-bg-strong text-st-warn-fg-strong',
  coi_t7: 'bg-st-warn-bg-strong text-st-warn-fg-strong',
  account_terminated: 'bg-st-warn-bg-strong text-st-warn-fg-strong',
  facility_terminated: 'bg-st-warn-bg-strong text-st-warn-fg-strong',
};

const DAY_OPTIONS = [7, 14, 30, 60, 90];

function eventLabel(t: string): string {
  return EVENT_LABELS[t] ?? t.replace(/_/g, ' ');
}

function fmtWhen(s: string): string {
  try {
    const d = new Date(s);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
      + ' ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch {
    return s;
  }
}

export default function FinanceRecentChangesPage() {
  const { profile } = useAuth();
  // Mirrors get_finance_recent_changes(): has_finance_capability(), hat included.
  const allowed = hasFinanceCapability(profile);

  const [rows, setRows] = useState<ChangeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(14);
  const [typeFilter, setTypeFilter] = useState<string>('all');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: e } = await supabase.rpc('get_finance_recent_changes', {
      p_days: days,
    });
    if (e) {
      setError(rpcErrorMessage(e, "Couldn't load recent changes"));
      setRows([]);
    } else {
      setRows((data ?? []) as ChangeRow[]);
    }
    setLoading(false);
  }, [days]);

  useEffect(() => {
    if (allowed) void load();
  }, [allowed, load]);

  const types = useMemo(() => {
    const seen = new Map<string, number>();
    for (const r of rows) seen.set(r.event_type, (seen.get(r.event_type) ?? 0) + 1);
    return Array.from(seen.entries()).sort((a, b) => b[1] - a[1]);
  }, [rows]);

  const visible = useMemo(
    () => (typeFilter === 'all' ? rows : rows.filter((r) => r.event_type === typeFilter)),
    [rows, typeFilter],
  );

  if (!allowed) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <EmptyState
          title="Finance access required"
          body="Recent changes are visible to finance, executive, and superuser roles."
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4 sm:p-6">
      <PageHeader
        title="Recent Changes"
        meta={`${visible.length} change${visible.length === 1 ? '' : 's'} · last ${days} days`}
      />
      <p className="text-xs text-tcs-text-muted">
        Material changes routed to Finance — sub swaps, rate changes, terminations,
        new-account starts, margin flags, and invoice gaps. Driven by the notification
        routing table; new event types appear here automatically.
      </p>

      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs font-medium text-tcs-text-muted" htmlFor="frc-days">
          Window
        </label>
        <select
          id="frc-days"
          className="min-h-[44px] rounded-md border border-tcs-border bg-tcs-field px-3 text-sm"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          {DAY_OPTIONS.map((d) => (
            <option key={d} value={d}>
              Last {d} days
            </option>
          ))}
        </select>
        <label className="text-xs font-medium text-tcs-text-muted" htmlFor="frc-type">
          Type
        </label>
        <select
          id="frc-type"
          className="min-h-[44px] rounded-md border border-tcs-border bg-tcs-field px-3 text-sm"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="all">All types ({rows.length})</option>
          {types.map(([t, n]) => (
            <option key={t} value={t}>
              {eventLabel(t)} ({n})
            </option>
          ))}
        </select>
      </div>

      {error && <ErrorState title="Couldn't load recent changes" body={error} onRetry={load} />}

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-16" block />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <EmptyState
          title="No recent changes"
          body="Nothing material has been routed to Finance in this window."
        />
      ) : (
        <ul className="space-y-2">
          {visible.map((r) => (
            <li key={r.notification_id}>
              <ChangeCard row={r} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ChangeCard({ row }: { row: ChangeRow }) {
  const tone = EVENT_TONE[row.event_type] ?? 'bg-tcs-surface text-tcs-text-muted';
  const inner = (
    <div className="flex min-h-[56px] flex-col gap-1 rounded-lg border border-tcs-border bg-tcs-raised p-3 transition-colors hover:border-tcs-accent/40 sm:flex-row sm:items-center sm:gap-3">
      <span
        className={`inline-flex w-fit shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}
      >
        {eventLabel(row.event_type)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-tcs-text">
          {row.title ?? row.entity_id}
        </div>
        {row.body && (
          <div className="truncate text-xs text-tcs-text-muted">{row.body}</div>
        )}
      </div>
      <div className="shrink-0 text-right text-xs text-tcs-text-muted">
        <div>{fmtWhen(row.created_at)}</div>
        {(row.actor_name ?? row.actor_eid) && <div>by {row.actor_name ?? row.actor_eid}</div>}
      </div>
    </div>
  );
  return row.target_url ? (
    <Link to={row.target_url} className="block">
      {inner}
    </Link>
  ) : (
    inner
  );
}
